import { useState } from "react";
import { Check, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { trackQuoteSubmit } from "@/lib/analytics";
import { SERVICES, COMPANY } from "@/data/site";

const CONTACT_METHODS = ["Phone", "Email", "WhatsApp"];
const input = "w-full border border-[var(--border)] px-4 py-3 text-sm text-[var(--ink)] focus:outline-none focus:border-[var(--oxblood)] transition-colors bg-white";
const Field = ({ label, children }) => (
  <div><label className="block text-[11px] uppercase tracking-[0.2em] text-[var(--muted)] mb-2">{label}</label>{children}</div>
);

export default function CommercialSurveyForm() {
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [form, setForm] = useState({
    name: "", company: "", email: "", phone: "", address: "", service: "", preferred_contact: "Phone", message: "", website: "",
  });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.address) return toast.error("Please fill in your contact details");
    if (!form.service) return toast.error("Please select the service required");
    setSubmitting(true);
    try {
      await api.post("/quotes", {
        name: form.name,
        company: form.company,
        email: form.email,
        phone: form.phone,
        address: form.address,
        property_category: "Commercial",
        services: [form.service],
        notes: form.message,
        preferred_contact: form.preferred_contact,
        source: "quote",
        website: form.website,
      });
      trackQuoteSubmit();
      setDone(true);
    } catch {
      toast.error("Sorry, we couldn't submit your request just now. Please call us on " + COMPANY.phone + " or try again shortly.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) return (
    <div className="text-center py-16 px-8 border border-[var(--border)]" data-testid="survey-success">
      <div className="w-16 h-16 rounded-full bg-[var(--oxblood)] text-white flex items-center justify-center mx-auto mb-6"><Check className="w-8 h-8" /></div>
      <h3 className="font-serif text-3xl mb-3">Thank you, {form.name.split(" ")[0]}.</h3>
      <p className="text-[var(--muted)]">We've received your site survey request and a member of the StoneCrest team will be in touch.</p>
    </div>
  );

  return (
    <form onSubmit={submit} className="space-y-6" data-testid="commercial-survey-form">
      <input type="text" name="website" tabIndex={-1} autoComplete="off" aria-hidden="true" value={form.website} onChange={(e) => set("website", e.target.value)} className="absolute left-[-9999px] w-0 h-0 opacity-0" />
      <div>
        <p className="text-[11px] uppercase tracking-[0.3em] text-[var(--oxblood)] mb-2">For Business</p>
        <h3 className="font-serif text-3xl md:text-4xl">Request a Commercial Site Survey</h3>
      </div>

      <div className="grid sm:grid-cols-2 gap-5">
        <Field label="Full Name *"><input data-testid="survey-name" value={form.name} onChange={(e) => set("name", e.target.value)} className={input} placeholder="Jane Smith" /></Field>
        <Field label="Company Name"><input data-testid="survey-company" value={form.company} onChange={(e) => set("company", e.target.value)} className={input} placeholder="Acme Ltd" /></Field>
        <Field label="Email *"><input data-testid="survey-email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className={input} placeholder="you@company.com" /></Field>
        <Field label="Phone Number *"><input data-testid="survey-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} className={input} placeholder="07…" /></Field>
      </div>

      <Field label="Property / Site Address *"><input data-testid="survey-address" value={form.address} onChange={(e) => set("address", e.target.value)} className={input} placeholder="Site name, street, town, postcode" /></Field>

      <div className="grid sm:grid-cols-2 gap-5">
        <Field label="Service Required *">
          <select data-testid="survey-service" value={form.service} onChange={(e) => set("service", e.target.value)} className={input}>
            <option value="">Select a service…</option>
            {SERVICES.map((s) => <option key={s.slug} value={s.name}>{s.name}</option>)}
            <option value="Multiple / Other">Multiple / Other</option>
          </select>
        </Field>
        <Field label="Preferred Contact Method">
          <div className="flex gap-2">
            {CONTACT_METHODS.map((m) => (
              <button type="button" key={m} data-testid={`survey-contact-${m.toLowerCase()}`} onClick={() => set("preferred_contact", m)} className={`flex-1 py-2.5 text-sm rounded-full border transition-colors ${form.preferred_contact === m ? "bg-[var(--oxblood)] text-white border-[var(--oxblood)]" : "border-[var(--border)] hover:border-[var(--oxblood)]"}`}>{m}</button>
            ))}
          </div>
        </Field>
      </div>

      <Field label="Message"><textarea data-testid="survey-message" value={form.message} onChange={(e) => set("message", e.target.value)} rows={3} className={input} placeholder="Tell us briefly about the site and what you need…" /></Field>

      <button type="submit" data-testid="survey-submit" disabled={submitting} className="bg-[var(--oxblood)] text-white px-10 py-4 rounded-full tracking-wide hover:bg-[var(--oxblood-hover)] transition-colors disabled:opacity-50 flex items-center gap-2">
        {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Sending…</> : "Request Site Survey"}
      </button>
    </form>
  );
}
